import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { initializeRazorpay, createRazorpayOrder, processRazorpayPayment, verifyRazorpayPayment } from '../utils/razorpay';
import { getCourseDownloadLink } from '../utils/courseLinks';
import { useDispatch } from 'react-redux';
import { addPurchasedCourse } from '../store/store';
import Reviews from './Reviews';
import Footer from './Footer';
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LockClosedIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  GiftIcon,
  UsersIcon,
  XMarkIcon,
  DocumentTextIcon,
  ComputerDesktopIcon,
  AcademicCapIcon,
  ArrowDownTrayIcon,
  InformationCircleIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';


const CourseDetail = () => {
  const [expandedModules, setExpandedModules] = useState(new Set([1, 2, 3])); // First 3 modules expanded by default
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [zoomedImageIndex, setZoomedImageIndex] = useState(null);
  const [zoomedImageType, setZoomedImageType] = useState(null); // 'carousel' or 'column'
  const dispatch = useDispatch();

  // Carousel data - 1 image and 4 videos for the digital bundle
  const carouselItems = [
    {
      type: 'image',
      src: '/images/products/security/hackerofferbanner.jpeg',
      title: 'Course Cover Image',
      alt: 'Hackers Library Bundle'
    },
    {
      type: 'video',
      url: "https://drive.google.com/file/d/1mQtSo8fmnNu-EQPzxtDEM2vFi8J_UdVi/preview"
    },
    {
      type: 'video',
      url: "https://drive.google.com/file/d/1bWZllzYhcgIvUg81IY8HFdG2s12Dzgge/preview"
    },
    {
      type: 'video',
      url: "https://drive.google.com/file/d/169uK3hZbuZrKnNTcFk9z_6gGkeF_BIl3/preview"
    },
    {
      type: 'video',
      url: "https://drive.google.com/file/d/1LHFyNWS8XsbHEoR-Ld3OukWRZXxer-XI/preview"
    }
  ];

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Show bonus modal after a short delay
    const timer = setTimeout(() => {
      setShowBonusModal(true);
    }, 1500);
    
    // Initialize Razorpay
    const initRazorpay = async () => {
      const loaded = await initializeRazorpay();
      setRazorpayLoaded(loaded);
    };
    initRazorpay();

    return () => clearTimeout(timer);
  }, []);

  // Column grid images array - imported from assets folder
  const columnImages = [
    require('../assets/IMG_1770.jpeg'),
    require('../assets/IMG_1771.jpeg'),
    require('../assets/IMG_1772.jpeg'),
    require('../assets/IMG_1774.jpeg'),
    require('../assets/IMG_1775.jpeg'),
    require('../assets/IMG_1776.jpeg'),
    require('../assets/IMG_1777.jpeg'),
    require('../assets/IMG_1778.jpeg'),
    require('../assets/IMG_1779.jpeg'),
    require('../assets/IMG_1780.jpeg'),
    require('../assets/IMG_1781.jpeg'),
    require('../assets/IMG_1782.jpeg'),
    require('../assets/IMG_1831.png'),
    require('../assets/IMG_1832.png'),
    require('../assets/IMG_1833.png')
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselItems.length) % carouselItems.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const course = {
    id: 1,
    title: "Hackers Library Bundle",
    category: "Cybersecurity",
    instructor: "Security Expert Team",
    duration: "50 hours",
    students: 44,
    rating: 3.5,
    price: 149,
    originalPrice: 2999,
    image: "/images/products/security/zero-to-mastery.webp",
    description: "Master cybersecurity fundamentals, ethical hacking, and penetration testing with hands-on labs.",
    longDescription: "Transform from a cybersecurity beginner to an expert with our comprehensive Zero to Hero Mastery Course. This course covers everything from basic security concepts to advanced techniques including quantum cryptography, behavioral analysis, and cutting-edge defense strategies.",
    features: [
      "50+ hours of video content",
      "Hands-on labs and exercises",
      "Real-world case studies",
      "Certification preparation",
      "Lifetime access",
      "Quantum cryptography",
      "Graphology & behavioral analysis"
    ],
    level: "Beginner to Advanced",
    isActive: true,
    modules: [
      {
        id: 1,
        title: "Introduction to Cybersecurity",
        duration: "3 hours",
        topics: [
          "Cybersecurity Fundamentals",
          "Security Mindset Development",
          "Ethical Hacking Principles",
          "Security Career Paths",
          "Industry Overview & Trends",
          "Security Tools Introduction",
          "Lab Environment Setup",
          "Security Best Practices"
        ],
        icon: ShieldCheckIcon
      },
      {
        id: 2,
        title: "Fundamentals & Core Principles",
        duration: "5 hours",
        topics: [
          "Core Cybersecurity Principles",
          "Computer Hardware Fundamentals",
          "Operating Systems Deep Dive",
          "Programming Languages for Security",
          "Linux Commands & File System",
          "Linux Tools & Command Line",
          "Security Architecture Basics",
          "Threat Modeling Fundamentals"
        ],
        icon: LockClosedIcon
      },
      {
        id: 3,
        title: "Networking & Network Hacking",
        duration: "7 hours",
        topics: [
          "Networking Fundamentals",
          "Network Protocols & Security",
          "Network Reconnaissance",
          "Network Vulnerability Assessment",
          "Network Exploitation Techniques",
          "Wireless Network Hacking",
          "Network Defense Strategies",
          "Session Hijacking Techniques",
          "Man in the Middle (MITM) Attacks",
          "Footprinting & Information Gathering"
        ],
        icon: LightBulbIcon
      },
      {
        id: 4,
        title: "Mobile & Social Engineering",
        duration: "6 hours",
        topics: [
          "Android Hacking Techniques",
          "WhatsApp Security & Hacking",
          "Advanced WhatsApp Hacking Methods",
          "Social Engineering Fundamentals",
          "Advanced Social Engineering",
          "Graphology Analysis for Security",
          "Behavioral Pattern Recognition",
          "Mobile Security Countermeasures",
          "Open Source Intelligence (OSINT)"
        ],
        icon: ExclamationTriangleIcon
      },
      {
        id: 5,
        title: "Malware & AI-Powered Hacking",
        duration: "7 hours",
        topics: [
          "Malware Development Fundamentals",
          "Advanced Malware Techniques",
          "RAT (Remote Access Trojan) Development",
          "Hacking Using AI & Machine Learning",
          "AI-Powered Attack Vectors",
          "Prompt Engineering for Security",
          "AI Security Tools & Frameworks",
          "Defending Against AI Attacks"
        ],
        icon: ShieldExclamationIcon
      },
      {
        id: 6,
        title: "Advanced Cryptography & Steganography",
        duration: "4 hours",
        topics: [
          "Cryptography Fundamentals",
          "Advanced Encryption Techniques",
          "Steganography Methods",
          "Digital Watermarking",
          "Quantum Cryptography",
          "Post-Quantum Cryptography",
          "Cryptographic Attacks & Defenses"
        ],
        icon: ShieldCheckIcon
      },
      {
        id: 7,
        title: "Hardware Hacking & Blockchain",
        duration: "4 hours",
        topics: [
          "Hardware Hacking Fundamentals",
          "IoT Device Security",
          "Embedded Systems Hacking",
          "Blockchain Technology",
          "Cryptocurrency Security",
          "Smart Contract Vulnerabilities",
          "Hardware Security Modules"
        ],
        icon: LockClosedIcon
      },
      {
        id: 8,
        title: "Red Team & Blue Team Operations",
        duration: "6 hours",
        topics: [
          "Red Team Methodology",
          "Advanced Penetration Testing",
          "Offensive Penetration Testing",
          "Blue Team Defense Strategies",
          "Incident Response & Forensics",
          "Threat Hunting Techniques",
          "Security Operations Center (SOC)",
          "Red vs Blue Team Exercises"
        ],
        icon: LightBulbIcon
      },
      {
        id: 9,
        title: "Threat Countermeasures & Defense",
        duration: "3 hours",
        topics: [
          "Threat Countermeasures",
          "Advanced Defense Strategies",
          "Security Monitoring & SIEM",
          "Endpoint Detection & Response",
          "Network Security Monitoring",
          "Security Automation",
          "Incident Response Planning"
        ],
        icon: ExclamationTriangleIcon
      },
      {
        id: 10,
        title: "Dark Web & Advanced Topics",
        duration: "3 hours",
        topics: [
          "Dark Web & Deep Web Exploration",
          "Dark Web Security & Anonymity",
          "Tor Network & Hidden Services",
          "Cryptocurrency in Dark Web",
          "Dark Web Intelligence Gathering",
          "Legal & Ethical Considerations",
          "Dark Web Defense Strategies"
        ],
        icon: ShieldCheckIcon
      },
      {
        id: 11,
        title: "Bug Bounty & Career Development",
        duration: "2 hours",
        topics: [
          "Bug Bounty Programs",
          "Vulnerability Research",
          "Career in Cybersecurity",
          "Industry Certifications",
          "Professional Development",
          "Networking & Community",
          "Next Steps & Resources",
          "Recommended Books & Research Papers"
        ],
        icon: ShieldCheckIcon
      }
    ],
    learningOutcomes: [
      "Kali Linux fundamentals and advanced usage",
      "Network enumeration and reconnaissance techniques",
      "Network hacking and exploitation methods",
      "Session hijacking and management",
      "Android penetration testing",
      "Social engineering tactics and countermeasures",
      "Mobile security assessment and protection",
      "Malware development using AI",
      "Social Engineering",
      "Hacking Using a smartphone",
      "Steganography and hidden data techniques",
      "Cryptography and encryption methods",
      "IoT and hardware hacking",
      "Building and flashing custom hardware",
      "Hardware threat analysis",
      "Countermeasures and security controls",
      "Cybersecurity principles and best practices",
      "Top standard recognized industry certifications",
      "Bug bounty hunting and vulnerability research"
    ],
    prerequisites: [
      "Basic computer literacy",
      "Fundamental networking knowledge",
      "Eagerness to learn cybersecurity",
      "No prior security experience required"
    ],
    certifications: [
      "Course Completion Certificate",
      "Hands-on Lab Certificates",
      "Industry-recognized Badges",
      "Continuing Education Credits"
    ],
    tools: [
      "Kali Linux",
      "Metasploit Framework",
      "Wireshark",
      "Nmap",
      "Burp Suite",
      "OWASP ZAP",
      "John the Ripper",
      "Hashcat",
      "Aircrack-ng",
      "SQLMap",
      "Nikto",
      "Dirb",
      "Hydra",
      "Social Engineer Toolkit (SET)",
      "Maltego"
    ]
  };

  const toggleModule = (moduleId) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    setExpandedModules(newExpanded);
  };

  const handleEnroll = async () => {
    if (!razorpayLoaded) {
      toast.error('Payment system is loading. Please try again.');
      return;
    }

    setIsLoading(true);
    try {
      // Create Razorpay order
      const orderData = await createRazorpayOrder(course.price, 'INR', `course_${course.id}`);
      
      // Process payment
      await processRazorpayPayment(
        orderData,
        course,
        async (response) => {
          // Payment successful
          try {
            // Verify payment
            const verification = await verifyRazorpayPayment(
              response.razorpay_payment_id,
              response.razorpay_order_id,
              response.razorpay_signature
            );

            if (verification.success) {
              // Add course to purchased list
              dispatch(addPurchasedCourse(course.id));
              // Meta Pixel Purchase event (client-side)
              if (typeof window !== 'undefined' && window.fbq) {
                try {
                  window.fbq('track', 'Purchase', { value: course.price, currency: 'INR' });
                } catch (e) {
                  console.warn('Meta Pixel tracking error:', e);
                }
              }
              
              toast.success('Payment successful! Opening download link...');
              
              // Get download link
              const downloadLink = getCourseDownloadLink(course.id);
              if (downloadLink) {
                window.open(downloadLink, '_blank');
              } else {
                toast.error('Download link not available. Please contact support.');
              }
            } else {
              toast.error('Payment verification failed.');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Payment verification failed.');
          }
        },
        (error) => {
          toast.error(error || 'Payment failed. Please try again.');
        }
      );
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <>
      <Helmet>
        <title>{course.title} | Pingfence</title>
        <meta name="description" content={course.longDescription} />
      </Helmet>

      {/* Bonus Modal */}
      {showBonusModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white border-4 border-black rounded-none max-w-md w-full p-4 sm:p-6 md:p-8 relative shadow-[8px_8px_0px_0px_#9FE240] md:shadow-[12px_12px_0px_0px_#9FE240] animate-in fade-in-0 zoom-in-95 duration-300">
            {/* Close Button */}
            <button
              onClick={() => setShowBonusModal(false)}
              className="absolute top-4 right-4 bg-white border-2 border-black p-1 text-black hover:bg-primary hover:-translate-y-1 hover:shadow-[2px_2px_0px_0px_#000000] transition-all"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>

            {/* Modal Content */}
            <div className="text-center mt-2">
              {/* Icon */}
              <div className="mx-auto w-16 h-16 bg-primary border-4 border-black flex items-center justify-center mb-6 shadow-[4px_4px_0px_0px_#000000]">
                <GiftIcon className="h-8 w-8 text-black" />
              </div>

              {/* Title */}
              <h3 className="text-3xl font-black text-black uppercase tracking-tighter mb-4" style={{textShadow: '2px 2px 0px #9FE240'}}>
                Exclusive Bonus Included!
              </h3>

              {/* Description */}
              <p className="text-black font-bold text-lg mb-6 leading-relaxed">
                You will get <span className="bg-black text-primary px-2 py-0.5 uppercase tracking-wide">350 Digital Assets + Community Access</span> with the Hackers Toolkit Digital Bundle absolutely FREE!
              </p>

              {/* Features */}
              <div className="bg-gray-100 border-2 border-black p-4 mb-8 text-left shadow-[4px_4px_0px_0px_#000000]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-primary border-2 border-black p-1">
                    <ShieldCheckIcon className="h-6 w-6 text-black" />
                  </div>
                  <span className="text-black font-black uppercase text-lg">350+ Digital Assets</span>
                </div>
                <p className="text-gray-800 font-bold text-sm ml-12">
                  Complete collection of hacking tools, scripts, exploits, and resources.
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setShowBonusModal(false)}
                  className="flex-1 bg-primary text-black font-black uppercase tracking-widest py-3 px-4 border-2 border-black shadow-[4px_4px_0px_0px_#000000] hover:shadow-[6px_6px_0px_0px_#000000] hover:-translate-y-1 hover:-translate-x-1 transition-all"
                >
                  Get Deal Now
                </button>
                <button
                  onClick={() => setShowBonusModal(false)}
                  className="flex-1 bg-white text-black font-black uppercase tracking-widest py-3 px-4 border-2 border-black shadow-[4px_4px_0px_0px_#000000] hover:bg-black hover:text-primary hover:shadow-[6px_6px_0px_0px_#9FE240] hover:-translate-y-1 hover:-translate-x-1 transition-all"
                >
                  Learn More
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28 lg:pb-8">
        
        {/* Breadcrumbs */}
        <nav className="flex flex-wrap items-center space-x-2 text-sm text-gray-800 mb-6 font-bold uppercase tracking-wide">
          <a href="/" className="hover:text-primary transition-colors text-black">Home</a>
          <ChevronRightIcon className="h-3 w-3 text-black" />
          <a href="/digital-bundles" className="hover:text-primary transition-colors text-black">Digital Bundles</a>
          <ChevronRightIcon className="h-3 w-3 text-black" />
          <span className="text-primary">{course.title}</span>
        </nav>

        {/* Two Column Layout Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
          
          {/* LEFT COLUMN: Media & Content */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Title for Mobile Only */}
            <div className="lg:hidden mb-4">
              <h1 className="text-3xl font-black text-black mb-2 uppercase tracking-tighter" style={{textShadow: '2px 2px 0px #9FE240'}}>{course.title}</h1>
              <p className="text-sm font-bold bg-primary inline-block px-2 py-1 border-2 border-black">By {course.instructor}</p>
            </div>

            {/* Main Media Gallery */}
            <div className="space-y-4">
              {/* Big Image/Video */}
              <div className="bg-white border-2 border-black aspect-video flex items-center justify-center shadow-[6px_6px_0px_0px_#000000] overflow-hidden relative group">
                {carouselItems[currentSlide].type === 'image' ? (
                  <img
                    src={carouselItems[currentSlide].src}
                    alt={carouselItems[currentSlide].alt}
                    className="w-full h-full object-cover filter contrast-125 saturate-150 cursor-pointer"
                    onClick={() => {
                      setZoomedImage(carouselItems[currentSlide].src);
                      setZoomedImageIndex(currentSlide);
                      setZoomedImageType('carousel');
                    }}
                  />
                ) : (
                  <iframe
                    src={carouselItems[currentSlide].url}
                    className="w-full h-full pointer-events-auto"
                    frameBorder="0"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    title={`Demo Video ${currentSlide}`}
                  ></iframe>
                )}
                {/* Navigation Arrows */}
                <button
                  onClick={(e) => { e.stopPropagation(); prevSlide(); }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white border-2 border-black hover:bg-primary text-black p-2 transition-all shadow-[2px_2px_0px_0px_#000000] z-10"
                >
                  <ChevronLeftIcon className="h-6 w-6 font-bold" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextSlide(); }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white border-2 border-black hover:bg-primary text-black p-2 transition-all shadow-[2px_2px_0px_0px_#000000] z-10"
                >
                  <ChevronRightIcon className="h-6 w-6 font-bold" />
                </button>
              </div>
              
              {/* Thumbnails & Demo Videos */}
              <div className="grid grid-cols-5 gap-2 sm:gap-4 mt-4">
                {carouselItems.map((item, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => goToSlide(idx)}
                    className={`cursor-pointer border-2 transition-all aspect-video overflow-hidden relative ${currentSlide === idx ? 'border-black shadow-[4px_4px_0px_0px_#9FE240]' : 'border-black opacity-60 hover:opacity-100 hover:shadow-[4px_4px_0px_0px_#000000]'}`}
                  >
                    {item.type === 'image' ? (
                      <img
                        src={item.src}
                        alt={item.alt}
                        className="w-full h-full object-cover filter contrast-125 saturate-150 pointer-events-none"
                      />
                    ) : (
                      <>
                        <div className="absolute inset-0 z-10"></div> {/* invisible overlay to catch clicks instead of iframe */}
                        <iframe
                          src={item.url}
                          className="w-full h-full pointer-events-none"
                          frameBorder="0"
                          title={`Demo Video Thumbnail ${idx}`}
                        ></iframe>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Content Tabs area (Simulated stacking) */}
            <div className="bg-white border-2 border-black p-6 md:p-8 space-y-12 shadow-[6px_6px_0px_0px_#000000]">
              
              {/* Description */}
              <section className="prose max-w-none">
                <h2 className="text-2xl font-black uppercase tracking-tight mb-4 flex items-center gap-2 text-black">
                  <LightBulbIcon className="h-6 w-6 text-primary font-bold" />
                  Overview
                </h2>
                <div className="text-black font-medium leading-relaxed whitespace-pre-line border-l-4 border-primary pl-4">
                  {course.longDescription}
                </div>
              </section>

              <hr className="border-black border-2" />

              {/* Key Features */}
              <section>
                <h2 className="text-2xl font-black uppercase tracking-tight mb-6 flex items-center gap-2 text-black">
                  <ShieldCheckIcon className="h-6 w-6 text-primary font-bold" />
                  Key Features
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {course.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 bg-gray-100 border-2 border-black p-4 shadow-[2px_2px_0px_0px_#9FE240]">
                      <CheckCircleIcon className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                      <span className="text-black font-bold text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </section>

              <hr className="border-black border-2" />

              {/* Column Layout with 15 Stock Images (Visual Proof) */}
              <section>
                <h2 className="text-2xl font-black uppercase tracking-tight mb-6 flex items-center gap-2 text-black">
                  <ShieldCheckIcon className="h-6 w-6 text-primary font-bold" />
                  Inside Your Bundle
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {columnImages.map((image, index) => (
                    <div key={index} className="bg-white border-2 border-black aspect-[3/4] shadow-[4px_4px_0px_0px_#000000] overflow-hidden cursor-pointer hover:-translate-y-1 hover:-translate-x-1 transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        setZoomedImage(image);
                        setZoomedImageIndex(index);
                        setZoomedImageType('column');
                      }}
                    >
                      <img
                        src={image}
                        alt={`Bundle Content ${index + 1}`}
                        className="w-full h-full object-cover filter contrast-125 saturate-150"
                      />
                    </div>
                  ))}
                </div>
              </section>

              <hr className="border-black border-2" />

              {/* ===== WHAT YOU ALSO GET SECTION ===== */}
              <section>
                <h2 className="text-2xl font-black uppercase tracking-tight mb-6 flex items-center gap-2 text-black">
                  <GiftIcon className="h-6 w-6 text-primary font-bold" />
                  What You Also Get
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-gray-100 border-2 border-black p-4 shadow-[2px_2px_0px_0px_#9FE240]">
                    <div className="flex items-start gap-3">
                      <GiftIcon className="h-6 w-6 text-black mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-black uppercase text-black text-sm mb-1">350+ Digital Assets</h3>
                        <p className="text-black font-medium text-xs">Professional hacking tools, scripts, & exploits worth ₹50,000+</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-100 border-2 border-black p-4 shadow-[2px_2px_0px_0px_#9FE240]">
                    <div className="flex items-start gap-3">
                      <UsersIcon className="h-6 w-6 text-black mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-black uppercase text-black text-sm mb-1">Lifetime Access</h3>
                        <p className="text-black font-medium text-xs">Forever access. No recurring fees. Download at your pace.</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-100 border-2 border-black p-4 shadow-[2px_2px_0px_0px_#9FE240]">
                    <div className="flex items-start gap-3">
                      <CheckCircleIcon className="h-6 w-6 text-black mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-black uppercase text-black text-sm mb-1">Certificate of Mastery</h3>
                        <p className="text-black font-medium text-xs">Showcase your expertise on LinkedIn, resume & portfolio.</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-100 border-2 border-black p-4 shadow-[2px_2px_0px_0px_#9FE240]">
                    <div className="flex items-start gap-3">
                      <ShieldCheckIcon className="h-6 w-6 text-black mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-black uppercase text-black text-sm mb-1">24/7 Expert Support</h3>
                        <p className="text-black font-medium text-xs">Join exclusive community. Connect with 44+ professionals.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <hr className="border-black border-2" />

              {/* ===== LEARNING OUTCOMES SECTION ===== */}
              <section>
                <h2 className="text-2xl font-black uppercase tracking-tight mb-6 flex items-center gap-2 text-black">
                  <AcademicCapIcon className="h-6 w-6 text-primary font-bold" />
                  Skills You'll Master
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {course.learningOutcomes.map((outcome, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-gray-100 border-2 border-black shadow-[2px_2px_0px_0px_#000000]">
                      <div className="w-2 h-2 bg-primary border border-black flex-shrink-0" />
                      <span className="text-black font-bold text-sm">{outcome}</span>
                    </div>
                  ))}
                </div>
              </section>


              <hr className="border-black border-2" />

              {/* ===== COURSE STRUCTURE SECTION ===== */}
              <section>
                <h2 className="text-2xl font-black uppercase tracking-tight mb-6 flex items-center gap-2 text-black">
                  <DocumentTextIcon className="h-6 w-6 text-primary font-bold" />
                  Complete Curriculum
                </h2>
                <div className="space-y-3">
                  {course.modules.slice(0, 5).map((module) => (
                    <div key={module.id} className="bg-white border-2 border-black overflow-hidden shadow-[2px_2px_0px_0px_#000000]">
                      <button
                        onClick={() => toggleModule(module.id)}
                        className="w-full p-4 md:p-6 flex items-center justify-between hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-4 text-left">
                          <div className="bg-primary border-2 border-black p-3 flex-shrink-0">
                            <module.icon className="h-6 w-6 text-black" />
                          </div>
                          <div>
                            <h3 className="text-lg font-black text-black">
                              Module {module.id}: {module.title}
                            </h3>
                            <p className="text-gray-800 text-sm font-bold">{module.duration}</p>
                          </div>
                        </div>
                        {expandedModules.has(module.id) ? (
                          <ChevronUpIcon className="h-5 w-5 text-black font-bold flex-shrink-0" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 text-black font-bold flex-shrink-0" />
                        )}
                      </button>
                      
                      {expandedModules.has(module.id) && (
                        <div className="px-4 md:px-6 pb-6 border-t-2 border-black bg-gray-50 pt-4">
                          <ul className="space-y-2">
                            {module.topics.slice(0, 5).map((topic, idx) => (
                              <li key={idx} className="flex items-center gap-3 text-black font-medium text-sm">
                                <div className="w-2 h-2 bg-black flex-shrink-0" />
                                {topic}
                              </li>
                            ))}
                            {module.topics.length > 5 && (
                              <li className="text-primary font-bold text-sm">+ {module.topics.length - 5} more topics</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="text-center mt-6">
                    <p className="text-black font-bold">... and {course.modules.length - 5} more modules covering 50+ hours of content</p>
                  </div>
                </div>
              </section>

              <hr className="border-black border-2" />

              {/* ===== TOOLS & TECHNOLOGIES ===== */}
              <section>
                <h2 className="text-2xl font-black uppercase tracking-tight mb-6 flex items-center gap-2 text-black">
                  <ComputerDesktopIcon className="h-6 w-6 text-primary font-bold" />
                  Professional Tools Included
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {course.tools.slice(0, 12).map((tool, index) => (
                    <div key={index} className="bg-gray-100 border-2 border-black p-3 text-center shadow-[2px_2px_0px_0px_#9FE240]">
                      <span className="text-black text-sm font-bold">{tool}</span>
                    </div>
                  ))}
                </div>
                <p className="text-center text-black font-bold mt-6">... and {course.tools.length - 12} more industry-standard tools</p>
              </section>

              <hr className="border-black border-2" />

              {/* ===== HACKERS WHATSAPP COMMUNITY SECTION ===== */}
              <section className="bg-primary/10 border-2 border-black p-6 md:p-8 shadow-[4px_4px_0px_0px_#000000]">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-1">
                    <h2 className="text-2xl font-black uppercase tracking-tight text-black mb-4 flex items-center gap-2">
                      <UsersIcon className="h-6 w-6 text-primary" />
                      Hackers Community
                    </h2>
                    <p className="text-black font-medium mb-4 text-sm leading-relaxed">
                      Connect with <span className="font-bold underline decoration-primary decoration-2">150+ real-world hackers</span> sharing free resources, tools, and exclusive security intelligence. Stay updated with the latest exploits.
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center gap-2 text-black text-sm font-bold">
                        <div className="w-2 h-2 bg-black" />
                        Daily security tips & techniques
                      </li>
                      <li className="flex items-center gap-2 text-black text-sm font-bold">
                        <div className="w-2 h-2 bg-black" />
                        Exclusive tools and scripts
                      </li>
                      <li className="flex items-center gap-2 text-black text-sm font-bold">
                        <div className="w-2 h-2 bg-black" />
                        Career guidance
                      </li>
                    </ul>
                    <button
                      onClick={() => window.open('https://chat.whatsapp.com/Ehbwe6E6nJa77r9k49kiFD?mode=wwt', '_blank', 'noopener,noreferrer')}
                      className="btn bg-black text-primary border-2 border-black w-full flex justify-center py-3 shadow-[4px_4px_0px_0px_#9FE240] hover:shadow-[6px_6px_0px_0px_#9FE240] hover:-translate-y-1 hover:-translate-x-1 transition-all"
                    >
                      Join Community Now
                    </button>
                  </div>
                  <div className="flex-1 flex justify-center items-center">
                    <div className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_#000000] p-6 text-center w-full max-w-xs">
                      <div className="text-5xl mb-2">💬</div>
                      <p className="text-black font-black uppercase text-lg mb-2">150+ Members</p>
                      <p className="text-gray-800 font-bold text-sm">Real hackers sharing real knowledge</p>
                    </div>
                  </div>
                </div>
              </section>

              <hr className="border-black border-2" />

              {/* ===== 1-1 MENTORSHIP SECTION ===== */}
              <section>
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-1 order-2 md:order-1">
                    <div className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_#000000] p-6">
                      <div className="mb-4">
                        <div className="text-4xl font-black text-black mb-2">🎓</div>
                        <h3 className="text-2xl font-black uppercase text-black mb-1">Aadhaar Koul</h3>
                        <p className="text-black font-bold text-sm bg-primary inline-block px-2 py-1 border-2 border-black">Grey Hat Hacker</p>
                      </div>
                      
                      <div className="space-y-2 mb-6">
                        <div className="flex items-center gap-2 text-black font-medium text-sm">
                          <CheckCircleIcon className="h-4 w-4 text-primary" />
                          <span>OSCP Certified</span>
                        </div>
                        <div className="flex items-center gap-2 text-black font-medium text-sm">
                          <CheckCircleIcon className="h-4 w-4 text-primary" />
                          <span>CEH (Certified Ethical Hacker)</span>
                        </div>
                        <div className="flex items-center gap-2 text-black font-medium text-sm">
                          <CheckCircleIcon className="h-4 w-4 text-primary" />
                          <span>CCNA & CCNP</span>
                        </div>
                      </div>

                      <a
                        href="https://www.linkedin.com/in/aadhaar-koul-7271b6204/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex justify-center bg-gray-100 hover:bg-gray-200 text-black border-2 border-black px-4 py-2 text-sm font-bold transition-all shadow-[2px_2px_0px_0px_#000000] w-full"
                      >
                        View LinkedIn Profile
                      </a>
                    </div>
                  </div>

                  <div className="flex-1 order-1 md:order-2">
                    <h2 className="text-2xl font-black uppercase tracking-tight text-black mb-4">
                      1-on-1 Mentorship
                    </h2>
                    <p className="text-black font-medium mb-4 text-sm leading-relaxed">
                      Get personalized guidance from a real-world grey hat hacker. Aadhaar Koul is open to mentoring students, clearing doubts, and providing career guidance.
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center gap-2 text-black text-sm font-bold">
                        <div className="w-2 h-2 bg-black" />
                        Personalized learning path
                      </li>
                      <li className="flex items-center gap-2 text-black text-sm font-bold">
                        <div className="w-2 h-2 bg-black" />
                        Doubt clearing sessions
                      </li>
                      <li className="flex items-center gap-2 text-black text-sm font-bold">
                        <div className="w-2 h-2 bg-black" />
                        Career guidance
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

            </div>

            {/* Reviews Section */}
            <div className="bg-white border-2 border-black p-6 md:p-8 shadow-[6px_6px_0px_0px_#000000]" id="reviews">
              <h2 className="text-3xl font-black uppercase tracking-tight mb-8 text-black" style={{textShadow: '2px 2px 0px #9FE240'}}>Customer Reviews</h2>
              <Reviews productId={1} />
            </div>

          </div>

          {/* RIGHT COLUMN: Sticky Sidebar Actions */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              
              {/* Product Header Card */}
              <div className="bg-white border-2 border-black p-6 shadow-[6px_6px_0px_0px_#000000]">
                
                {/* Title & Meta (Desktop) */}
                <div className="hidden lg:block mb-6">
                  <h1 className="text-3xl xl:text-4xl font-black text-black mb-2 leading-tight uppercase tracking-tighter">{course.title}</h1>
                  <p className="inline-block bg-primary border-2 border-black px-2 py-1 text-sm text-black font-bold hover:translate-x-1 hover:-translate-y-1 hover:shadow-[2px_2px_0px_0px_#000000] cursor-pointer transition-all">By {course.instructor}</p>
                  
                  {/* Rating summary */}
                  <div className="flex items-center gap-2 mt-3 text-sm">
                    <div className="flex text-yellow-500">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <StarIcon key={star} className="h-4 w-4 fill-current" />
                      ))}
                    </div>
                    <a href="#reviews" className="text-gray-800 hover:text-black font-bold transition-colors">(44+ Students)</a>
                  </div>
                </div>

                {/* Pricing & CTA */}
                <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-4 border-black p-4 pb-8 shadow-[0_-4px_0px_0px_#000000] lg:static lg:bg-transparent lg:border-none lg:p-0 lg:shadow-none mb-0 lg:mb-6 lg:pb-6 border-b-0 lg:border-b-2 border-black">
                  <div className="flex items-center justify-between lg:flex-col lg:items-start lg:gap-0 gap-4 mb-0 lg:mb-6 max-w-7xl mx-auto">
                    <div className="flex flex-col gap-1 lg:gap-0">
                      <div className="flex items-baseline gap-2 mb-0 lg:mb-2">
                        <span className="text-3xl lg:text-5xl font-black text-black" style={{textShadow: '2px 2px 0px #9FE240'}}>
                          ₹{course.price.toLocaleString('en-IN')}
                        </span>
                        <span className="hidden lg:inline-block text-sm font-bold text-gray-800 bg-gray-100 border-2 border-black px-2 py-0.5">Tax included</span>
                      </div>
                      <div className="mb-0 lg:mb-6 hidden lg:block">
                        <div className="flex items-center mb-0 lg:mb-3">
                          <span className="text-sm lg:text-lg text-gray-800 line-through font-bold">₹{course.originalPrice.toLocaleString('en-IN')}</span>
                          <span className="ml-2 bg-black text-primary px-2 py-1 text-xs font-bold uppercase border-2 border-black">
                            Save {Math.round((1 - course.price/course.originalPrice) * 100)}%
                          </span>
                        </div>
                      </div>
                      <div className="lg:hidden text-xs font-bold text-black border-2 border-black bg-primary px-1 py-0.5 inline-block w-fit">
                        Save {Math.round((1 - course.price/course.originalPrice) * 100)}%
                      </div>
                    </div>

                    <div className="flex-1 lg:w-full">
                      <button 
                        onClick={handleEnroll}
                        disabled={isLoading || !razorpayLoaded}
                        className="btn btn-primary w-full flex items-center justify-center space-x-2 text-base lg:text-lg py-3 shadow-[4px_4px_0px_0px_#000000] hover:shadow-[6px_6px_0px_0px_#000000] hover:-translate-y-1 hover:-translate-x-1"
                      >
                        {isLoading ? (
                          <>
                            <div className="animate-spin h-5 w-5 border-b-2 border-black"></div>
                            <span>Processing...</span>
                          </>
                        ) : (
                          <>
                            <CreditCardIcon className="h-5 w-5 lg:h-6 lg:w-6 font-bold" />
                            <span>Buy Now</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="hidden lg:block">
                    <p className="text-center text-xs text-black font-bold mt-4 flex items-center justify-center gap-1">
                      <ShieldCheckIcon className="h-4 w-4 text-primary" /> Secure Razorpay transaction
                    </p>
                    <p className="text-center text-xs text-black font-bold mt-1">
                      Receive the Google drive link in under a minute.
                    </p>
                  </div>
                </div>

                {/* Metadata List */}
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between items-center text-black font-medium">
                    <div className="flex items-center gap-2 text-black font-bold">
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Delivery
                    </div>
                    <span className="font-bold text-black">Instant Download</span>
                  </div>
                  <div className="flex justify-between items-center text-black font-medium">
                    <div className="flex items-center gap-2 text-black font-bold">
                      <ComputerDesktopIcon className="h-4 w-4" />
                      Access
                    </div>
                    <span className="font-bold text-black">Lifetime</span>
                  </div>
                  <div className="flex justify-between items-center text-black font-medium">
                    <div className="flex items-center gap-2 text-black font-bold">
                      <DocumentTextIcon className="h-4 w-4" />
                      Contents
                    </div>
                    <span className="font-bold text-primary underline">350+ Assets</span>
                  </div>
                  <div className="flex justify-between items-center text-black font-medium">
                    <div className="flex items-center gap-2 text-black font-bold">
                      <CheckCircleIcon className="h-4 w-4" />
                      Guarantee
                    </div>
                    <span className="font-bold text-black">30-Days Money-back</span>
                  </div>
                </div>

              </div>

              {/* Support Card */}
              <div className="bg-gray-100 border-2 border-black p-5 shadow-[4px_4px_0px_0px_#000000]">
                <h3 className="font-black uppercase tracking-wide text-black mb-2 flex items-center gap-2">
                  <InformationCircleIcon className="h-5 w-5" />
                  Need Help?
                </h3>
                <p className="text-sm font-medium text-black mb-4">If you encounter any issues or have questions about this bundle, our team is ready to assist.</p>
                <a href="/contact" className="btn bg-white text-black border-2 border-black shadow-[2px_2px_0px_0px_#000000] inline-flex text-sm py-2 px-4 hover:-translate-y-1 hover:shadow-[4px_4px_0px_0px_#000000] transition-all">Contact Support &rarr;</a>
              </div>

            </div>
          </div>
        </div>

      </div>

      <Footer />

      {/* Image Zoom Modal - Full Screen Lightbox */}
      {zoomedImage && (
        <div
          className="fixed inset-0 bg-black z-50 flex items-center justify-center p-4"
          onClick={() => setZoomedImage(null)}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={zoomedImage}
              alt="Zoomed"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Navigation Arrows - show if zoomed image is from carousel or column */}
            {zoomedImageIndex !== null && zoomedImageIndex >= 0 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    let prevIndex = zoomedImageIndex - 1;
                    
                    if (zoomedImageType === 'carousel') {
                      if (prevIndex < 0) {
                        prevIndex = 4; // Loop back to last carousel item (first 5 items, so index 4)
                      }
                      const prevItem = carouselItems[prevIndex];
                      if (prevItem) {
                        setZoomedImageIndex(prevIndex);
                        setZoomedImage(prevItem.src);
                      }
                    } else if (zoomedImageType === 'column') {
                      if (prevIndex < 0) {
                        prevIndex = 14; // Loop back to last column image (15 images, so index 14)
                      }
                      const prevImage = columnImages[prevIndex];
                      if (prevImage) {
                        setZoomedImageIndex(prevIndex);
                        setZoomedImage(prevImage);
                      }
                    }
                  }}
                  className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-primary hover:bg-primary/80 text-black p-4 rounded-full transition-all z-20 hover:scale-110"
                >
                  <ChevronLeftIcon className="h-8 w-8" />
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    let nextIndex = zoomedImageIndex + 1;
                    
                    if (zoomedImageType === 'carousel') {
                      if (nextIndex > 4) {
                        nextIndex = 0; // Loop back to first carousel item
                      }
                      const nextItem = carouselItems[nextIndex];
                      if (nextItem) {
                        setZoomedImageIndex(nextIndex);
                        setZoomedImage(nextItem.src);
                      }
                    } else if (zoomedImageType === 'column') {
                      if (nextIndex > 14) {
                        nextIndex = 0; // Loop back to first column image (15 images)
                      }
                      const nextImage = columnImages[nextIndex];
                      if (nextImage) {
                        setZoomedImageIndex(nextIndex);
                        setZoomedImage(nextImage);
                      }
                    }
                  }}
                  className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-primary hover:bg-primary/80 text-black p-4 rounded-full transition-all z-20 hover:scale-110"
                >
                  <ChevronRightIcon className="h-8 w-8" />
                </button>
              </>
            )}
            
            <button
              onClick={() => setZoomedImage(null)}
              className="absolute top-6 right-6 bg-primary hover:bg-primary/80 text-black p-3 rounded-full transition-all z-30 hover:scale-110"
            >
              <XMarkIcon className="h-7 w-7" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CourseDetail;
 
